import { KeysToCamelCase, NavigationLinkMapping } from '@statsbomb/parachute-types';
import { Atom, atom, Getter } from 'jotai';
import { atomWithStorage, unwrap } from 'jotai/utils';
import { locationAtom } from '@/atoms/location';
import { LinkConfig } from '@/types/navigation';
import { globalPlayerIdAtom } from './player/player';
import {
  playerNavigationGameLinkIdAtom,
  playerNavigationCompetitionLinkIdAtom,
  playerNavigationKeeperLinkIdAtom,
  playerNavigationTeamLinkIdAtom,
  playerIdMappingAtom,
} from './queries/players';
import {
  teamNavigationGameLinkIdAtom,
  teamNavigationCompetitionLinkIdAtom,
  teamNavigationKeeperLinkIdAtom,
  teamNavigationPlayerLinkIdAtom,
  userDefaultTeamIdsAtom,
  teamIdMappingAtom,
} from './queries/teams';
import { globalTeamIdAtom } from './team/team';

export const navigationOpenAtom = atomWithStorage('navigationOpen', false);

const playerPageLink: LinkConfig = {
  path: '/:language/player/:playerId/dashboard',
  relative: 'path',
  params: [],
  removeOtherParams: true,
};

export const playerPreviewLink: LinkConfig = {
  path: 'player/preview',
  relative: 'route',
  params: ['playerId'],
};

const teamPageLink: LinkConfig = {
  path: '/:language/team/:teamId/dashboard',
  relative: 'path',
  params: [],
  removeOtherParams: true,
};

export const videoPreviewPageLink: LinkConfig = {
  path: 'video/preview',
  relative: 'route',
  params: [],
};

export const previewPagePathLink: LinkConfig = {
  path: 'preview',
  relative: 'path',
  params: [],
};

const previewPageLink: LinkConfig = {
  path: 'preview',
  relative: 'route',
  params: [],
};

// don't go anywhere
export const samePageLink: LinkConfig = {
  path: '',
  relative: 'route',
  params: [],
};

// go to the current page relative to the component route
export const routeCurrentPageLink: LinkConfig = {
  path: '.',
  relative: 'route',
  params: [],
};

export const playerLinkAtom = atom<LinkConfig>(playerPageLink);

export const teamLinkAtom = atom<LinkConfig>(teamPageLink);

export const eventLinkAtom = atom<LinkConfig>(previewPageLink);

export const videoLinkAtom = atom<LinkConfig>(get =>
  get(locationAtom).pathname.includes('/vis') ? previewPagePathLink : videoPreviewPageLink,
);

const navigationId = async (
  get: Getter,
  playerNavigationLinkIdAtom: Atom<Promise<KeysToCamelCase<NavigationLinkMapping>>>,
  teamNavigationLinkIdAtom: Atom<Promise<KeysToCamelCase<NavigationLinkMapping>>>,
) => {
  const playerNavigationLinkId = (await get(playerNavigationLinkIdAtom)).iqId;
  if (playerNavigationLinkId) return playerNavigationLinkId;
  const teamNavigationLinkId = (await get(teamNavigationLinkIdAtom)).iqId;
  return teamNavigationLinkId || 0;
};

const navigationGameIdAtom = atom(async get =>
  navigationId(get, playerNavigationGameLinkIdAtom, teamNavigationGameLinkIdAtom),
);

const navigationCompetitionIdAtom = atom(async get =>
  navigationId(get, playerNavigationCompetitionLinkIdAtom, teamNavigationCompetitionLinkIdAtom),
);

const navigationKeeperIdAtom = atom(async get =>
  navigationId(get, playerNavigationKeeperLinkIdAtom, teamNavigationKeeperLinkIdAtom),
);

/**
 * Unlike the other navigation id atoms, these return both the DataHub ID and the IQ ID, since the
 * one we need is dependent upon the user's chosen experience.
 */
export const navigationPlayerIdAtom = atom(async get => {
  const playerIdMapping = await get(playerIdMappingAtom);
  const globalPlayerId = get(globalPlayerIdAtom);
  if (globalPlayerId)
    return {
      datahubId: globalPlayerId,
      iqId: globalPlayerId ? playerIdMapping[globalPlayerId] : null,
    };
  return get(teamNavigationPlayerLinkIdAtom);
});

export const navigationTeamIdAtom = atom(async get => {
  const selectedPlayerTeamId = await get(playerNavigationTeamLinkIdAtom);
  // check that the ids are not the fallback zeros
  if (!!selectedPlayerTeamId.iqId && !!selectedPlayerTeamId.datahubId) return selectedPlayerTeamId;

  const globalTeamId = get(globalTeamIdAtom);
  // Only await the team id mapping on the branch that actually uses it. Previously
  // it was awaited unconditionally, which serialised /teams/mapping ahead of the
  // default-team lookup below on the initial-load path (globalTeamId not yet set) —
  // even though that path never uses the mapping. /teams/mapping is still fetched in
  // the background via useApplicationReferenceData, so nothing is delayed.
  if (globalTeamId) {
    const teamIdMapping = await get(teamIdMappingAtom);
    return {
      datahubId: globalTeamId,
      iqId: teamIdMapping[globalTeamId] ?? null,
    };
  }

  const userDefaultTeamIds = await get(userDefaultTeamIdsAtom);
  if (userDefaultTeamIds) return userDefaultTeamIds;

  return { datahubId: 0, iqId: 0 };
});

export const unwrappedNavigationGameIdAtom = unwrap(navigationGameIdAtom, prev => prev ?? 0);
export const unwrappedNavigationCompetitionIdAtom = unwrap(navigationCompetitionIdAtom, prev => prev ?? 0);
export const unwrappedNavigationKeeperIdAtom = unwrap(navigationKeeperIdAtom, prev => prev ?? 0);
export const unwrappedNavigationPlayerIdAtom = unwrap(
  navigationPlayerIdAtom,
  prev => prev ?? { datahubId: 0, iqId: null },
);
export const unwrappedNavigationTeamIdAtom = unwrap(navigationTeamIdAtom, prev => prev ?? { datahubId: 0, iqId: 0 });
